<template lang="pug">
v-dialog(
  v-model="dialogOpen",
  :max-width="maxWidth",
  :width="maxWidth",
  transition="dialog-transition"
)
  v-card.companies-dialog(v-if="dialogOpen")
    .companies-dialog__header
      .companies-dialog__heading
        .companies-dialog__title {{ title }}
        .companies-dialog__subtitle(v-if="itemLabel") {{ itemLabel }}

    v-divider

    .companies-dialog__body
      v-data-table.companies-dialog__table(
        :headers="headers",
        :items="details",
        :loading="loading",
        :items-per-page="10",
        :footer-props="footerProps",
        locale="pt-BR",
        no-data-text="Nenhum registro encontrado"
      )
        template(v-slot:item.acessos="{ item }") {{ formatInteger(item.acessos) }}
        template(v-slot:item.usuarios="{ item }") {{ formatInteger(item.usuarios) }}
        template(v-slot:item.acoesRealizadas="{ item }") {{ formatInteger(item.acoesRealizadas) }}
        template(v-slot:item.entradasFuncionalidades="{ item }") {{ formatInteger(item.entradasFuncionalidades) }}
        template(v-slot:item.tempoUso="{ item }") {{ formatMinutos(item.tempoUso) }}

        template(v-slot:body.append, v-if="details.length")
          tr.companies-dialog__total-row
            td.companies-dialog__total-label Total
            td.text-end {{ totais.acessos }}
            td.text-end {{ totais.usuarios }}
            td.text-end {{ totais.acoesRealizadas }}
            td.text-end {{ totais.entradasFuncionalidades }}
            td.text-end {{ totais.tempoUso }}

    v-divider

    v-card-actions.companies-dialog__actions
      v-spacer
      v-btn.mr-1(
        color="primary",
        text,
        @click="exportarCSV",
        :disabled="!details.length || loading"
      )
        v-icon(left, small) mdi-file-table-outline
        span Exportar
      v-btn(color="primary", text, @click="dialogOpen = false")
        v-icon(left, small) mdi-close
        span Fechar
</template>

<script>
import csapi from "@/api/csapi";
import { msParaMinutos, formatMinutos } from "@/utils/tempo";
import { normalizeClientDisplayName } from "@/utils/cliente";

const NUMBER_FORMATTER = new Intl.NumberFormat("pt-BR");

const DETAIL_HEADERS = [
  { text: "Cliente (Base de dados)", value: "cliente" },
  { text: "Acessos", value: "acessos", align: "end", width: "150px" },
  { text: "Usuários", value: "usuarios", align: "end", width: "150px" },
  { text: "Ações realizadas", value: "acoesRealizadas", align: "end", width: "150px" },
  {
    text: "Entradas nas funcionalidades",
    value: "entradasFuncionalidades",
    align: "end",
    width: "150px"
  },
  { text: "Tempo de uso (min)", value: "tempoUso", align: "end", width: "150px" }
];

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatInteger(value) {
  return NUMBER_FORMATTER.format(Math.round(toNumber(value)));
}

function somarTotais(clientes) {
  return clientes.reduce(
    (acc, item) => ({
      acessos: acc.acessos + toNumber(item.acessos),
      usuariosDistintos: acc.usuariosDistintos + toNumber(item.usuariosDistintos),
      acoesRealizadas: acc.acoesRealizadas + toNumber(item.acoesRealizadas),
      entradasFuncionalidades: acc.entradasFuncionalidades + toNumber(item.entradasFuncionalidades),
      tempoUsoMs: acc.tempoUsoMs + toNumber(item.tempoUsoMs)
    }),
    {
      acessos: 0,
      usuariosDistintos: 0,
      acoesRealizadas: 0,
      entradasFuncionalidades: 0,
      tempoUsoMs: 0
    }
  );
}

export default {
  name: "ClientesFuncionalidadeDialog",

  props: {
    value: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: "Clientes da funcionalidade"
    },
    itemLabel: {
      type: String,
      default: ""
    },
    body: {
      type: Object,
      default: () => ({})
    },
    maxWidth: {
      type: [Number, String],
      default: 1280
    }
  },

  data: () => ({
    headers: DETAIL_HEADERS,
    details: [],
    total: null,
    loading: false,
    footerProps: {
      itemsPerPageOptions: [10, 25, 50, 100, -1],
      showCurrentPage: true,
      showFirstLastPage: true,
      itemsPerPageText: "Itens por página",
      itemsPerPageAllText: "Todos"
    }
  }),

  computed: {
    dialogOpen: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      }
    },

    totais() {
      const total = this.total || {};
      return {
        acessos: formatInteger(total.acessos),
        usuarios: formatInteger(total.usuariosDistintos),
        acoesRealizadas: formatInteger(total.acoesRealizadas),
        entradasFuncionalidades: formatInteger(total.entradasFuncionalidades),
        tempoUso: formatMinutos(msParaMinutos(total.tempoUsoMs))
      };
    }
  },

  watch: {
    dialogOpen: {
      immediate: true,
      handler(open) {
        if (open) {
          this.fetchDetails();
        }
      }
    }
  },

  methods: {
    formatInteger,
    formatMinutos,

    async fetchDetails() {
      if (!this.body || !this.body.funcionalidade) {
        this.details = [];
        this.total = null;
        return;
      }

      this.loading = true;
      this.details = [];
      this.total = null;

      try {
        const response = await csapi.post(
          "/v1/analytics/dashboard/graficos/detalhes-alcance",
          this.body
        );
        const data = response.data || {};
        const clientes = Array.isArray(data.clientes) ? data.clientes : [];

        this.details = clientes.map(item => ({
          cliente: normalizeClientDisplayName(item.cliente) || "-",
          acessos: toNumber(item.acessos),
          usuarios: toNumber(item.usuariosDistintos),
          acoesRealizadas: toNumber(item.acoesRealizadas),
          entradasFuncionalidades: toNumber(item.entradasFuncionalidades),
          tempoUso: msParaMinutos(item.tempoUsoMs)
        }));
        this.total = data.total || somarTotais(clientes);
      } catch (error) {
        this.$toast.error("Erro ao carregar os clientes da funcionalidade.");
      } finally {
        this.loading = false;
      }
    },

    getExportFileName(extension) {
      const base = this.itemLabel || this.title || "Clientes";
      const date = new Date().toLocaleDateString("pt-BR");
      return `${base} - ${date}.${extension}`;
    },

    exportarCSV() {
      try {
        if (!this.details.length) {
          this.$toast.error("Não há dados para exportar");
          return;
        }

        let csvContent = this.headers.map(header => `"${header.text}"`).join(";") + "\n";

        const linhaTotal = {
          cliente: "Total",
          acessos: this.totais.acessos,
          usuarios: this.totais.usuarios,
          acoesRealizadas: this.totais.acoesRealizadas,
          entradasFuncionalidades: this.totais.entradasFuncionalidades,
          tempoUso: this.totais.tempoUso
        };

        const formatters = {
          acessos: formatInteger,
          usuarios: formatInteger,
          acoesRealizadas: formatInteger,
          entradasFuncionalidades: formatInteger,
          tempoUso: formatMinutos
        };

        [...this.details, linhaTotal].forEach(item => {
          const row = this.headers.map(header => {
            const value = item[header.value];
            if (value === null || value === undefined) return '""';
            const formatter = formatters[header.value];
            const texto = formatter && typeof value === "number" ? formatter(value) : String(value);
            return `"${texto.replace(/"/g, '""')}"`;
          });
          csvContent += row.join(";") + "\n";
        });

        const blob = new Blob(["\ufeff" + csvContent], {
          type: "text/csv;charset=utf-8;"
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.setAttribute("href", url);
        link.setAttribute("download", this.getExportFileName("csv"));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        this.$toast.success("Arquivo CSV exportado com sucesso!");
      } catch (error) {
        this.$toast.error("Erro ao exportar os dados");
      }
    }
  }
};
</script>

<style scoped>
.companies-dialog {
  border-radius: 10px !important;
  overflow: hidden;
}

.companies-dialog__header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #eef1ff 0%, #f7f8ff 100%);
}

.companies-dialog__heading {
  flex: 1 1 auto;
  min-width: 0;
}

.companies-dialog__title {
  color: #0f2b57;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
}

.companies-dialog__subtitle {
  color: #5b6f90;
  font-size: 13px;
  font-weight: 500;
  margin-top: 2px;
}

.companies-dialog__body {
  padding: 16px 24px;
}

.companies-dialog__table {
  border-radius: 10px;
  border: 1px solid #d9e2ec;
}

.companies-dialog__table >>> .v-data-table__wrapper {
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: auto;
  scrollbar-width: thin;
}

.companies-dialog__total-row {
  background-color: #f4f7fb;
}

.companies-dialog__total-row td {
  border-top: 1px solid #d9e2ec !important;
  color: #0f2b57;
  font-weight: 700;
}

.companies-dialog__total-label {
  color: #0f2b57;
  font-weight: 700;
}

.companies-dialog__actions {
  padding: 12px 20px !important;
}
</style>
